import { NULLIFIER_BYTES, SECRET_BYTES, FIELD_SIZE_BLS12_381 } from "./constants";
import { buildPoseidon } from "./poseidon";
/**
 * Isomorphic CSPRNG. Uses Web Crypto when available (browsers, Node 19+),
 * which is the universally-supported path. We do NOT fall back to Math.random
 * for any cryptographic value — secrets MUST come from a CSPRNG.
 *
 * `globalThis.crypto.getRandomValues` is available in:
 *   - All modern browsers (since ~2014)
 *   - Node.js 19.0.0+ (globally; older versions need `node:crypto.webcrypto`)
 *   - Cloudflare Workers, Deno, Bun
 */
function getRandomBytes(n) {
    const out = new Uint8Array(n);
    const c = globalThis.crypto;
    if (!c || typeof c.getRandomValues !== "function") {
        throw new Error("No CSPRNG available. This runtime lacks `crypto.getRandomValues` — " +
            "needs Node 19+ or a modern browser/Worker.");
    }
    c.getRandomValues(out);
    return out;
}
function randomFieldElement(bytes) {
    // Rejection sampling: keep regenerating until we land within the field.
    // With 31 bytes (248 bits) and a 255-bit field, collisions are extremely
    // unlikely but we still loop for correctness.
    while (true) {
        const buf = getRandomBytes(bytes);
        // Hex-encode without using Buffer (which is Node-only and bundles
        // heavily in browser builds).
        let hex = "";
        for (const b of buf)
            hex += b.toString(16).padStart(2, "0");
        const value = BigInt("0x" + hex);
        if (value < FIELD_SIZE_BLS12_381)
            return value;
    }
}
export async function createDeposit(opts = {}) {
    const nullifier = opts.nullifier ?? randomFieldElement(NULLIFIER_BYTES);
    const secret = opts.secret ?? randomFieldElement(SECRET_BYTES);
    const poseidon = await buildPoseidon();
    const commitment = await poseidon([nullifier, secret]);
    const nullifierHash = await poseidon([nullifier]);
    return { nullifier, secret, commitment, nullifierHash };
}
export function leafBytes(deposit) {
    // Concatenated little-endian, 32 bytes each (matches the circom signal
    // serialization assumed by the verifier wrapper).
    return concatLE32(deposit.nullifier, deposit.secret);
}
function concatLE32(a, b) {
    const out = new Uint8Array(64);
    writeLE32(out, 0, a);
    writeLE32(out, 32, b);
    return out;
}
function writeLE32(buf, offset, value) {
    let v = value;
    for (let i = 0; i < 32; i++) {
        buf[offset + i] = Number(v & 0xffn);
        v >>= 8n;
    }
}
//# sourceMappingURL=commitment.js.map